import React, { useState, useEffect } from 'react';
import DeliveryMapPin from '../checkout/DeliveryMapPin';
import { useCart } from '../../context/CartContext';
import { deliveryZonesApi } from '../../services/api';

const LABELS = ['Home', 'Office', 'Other'];

const validatePhoneNumber = (phone) => {
  const cleaned = phone.replace(/[^\d+]/g, '').replace(/^\+/, '');
  if (cleaned.startsWith('0')) {
    if (cleaned.length !== 10) {
      return { valid: false, error: 'Phone number must be 10 digits (e.g. 0712345678)' };
    }
    const subscriber = cleaned.slice(1);
    if (!/^[71]/.test(subscriber)) {
      return { valid: false, error: 'Invalid Kenyan mobile number' };
    }
    return { valid: true, formatted: `254${subscriber}` };
  }
  if (cleaned.startsWith('254')) {
    if (cleaned.length !== 12) {
      return { valid: false, error: 'Phone number must be 12 digits with country code (e.g. 254712345678)' };
    }
    const subscriber = cleaned.slice(3);
    if (!/^[71]/.test(subscriber)) {
      return { valid: false, error: 'Invalid Kenyan mobile number' };
    }
    return { valid: true, formatted: cleaned };
  }
  if (cleaned.length === 9 && /^[71]/.test(cleaned)) {
    return { valid: true, formatted: `254${cleaned}` };
  }
  return { valid: false, error: 'Phone number must start with 0, 254, or +254' };
};

const DEFAULT_FORM = {
  label: 'Home',
  apartment_number: '',
  building: '',
  street: '',
  city: '',
  county: '',
  landmark: '',
  receiver_name: '',
  receiver_phone: '',
  is_default: false,
  delivery_lat: null,
  delivery_lng: null,
};

const inputClass = (hasError) =>
  `w-full border p-3 rounded-lg focus:outline-none focus:ring-2 text-sm ${
    hasError
      ? 'border-red-500 focus:ring-red-300'
      : 'border-gray-300 focus:ring-gold focus:border-gold'
  }`;

const AddressForm = ({ initialValues, onSubmit, onCancel, submitLabel = 'Save Address' }) => {
  const [form, setForm] = useState(DEFAULT_FORM);
  const [errors, setErrors] = useState({});
  const [pinError, setPinError] = useState(false);
  const [isZoneCheckLoading, setIsZoneCheckLoading] = useState(false);

  const { zoneCheckResult, setZoneCheckResult, clearZoneCheckResult } = useCart();

  useEffect(() => {
    if (initialValues) {
      setForm({
        label: initialValues.label || '',
        apartment_number: initialValues.apartment_number || '',
        building: initialValues.building || '',
        street: initialValues.street || '',
        city: initialValues.city || '',
        county: initialValues.county || '',
        landmark: initialValues.landmark || '',
        receiver_name: initialValues.receiver_name || '',
        receiver_phone: initialValues.receiver_phone || '',
        is_default: initialValues.is_default || false,
        delivery_lat: initialValues.delivery_lat || null,
        delivery_lng: initialValues.delivery_lng || null,
      });
    }
  }, [initialValues]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleLabelSelect = (label) => {
    setForm(prev => ({ ...prev, label }));
  };

  const handlePinChange = ({ lat, lng }) => {
    setForm(prev => ({ ...prev, delivery_lat: lat, delivery_lng: lng }));
    if (lat && lng) setPinError(false);
    if (lat && lng) {
      setIsZoneCheckLoading(true);
      deliveryZonesApi.check(lat, lng)
        .then(data => setZoneCheckResult(data))
        .catch(() => setZoneCheckResult(null))
        .finally(() => setIsZoneCheckLoading(false));
    } else {
      clearZoneCheckResult();
    }
  };

  const handleCountyDetected = (county) => {
    if (county) {
      setForm(prev => ({ ...prev, county }));
    }
  };

  const validate = () => {
    if (zoneCheckResult?.out_of_range === true) return false;
    const newErrors = {};
    if (!form.label) newErrors.label = 'Please select an address type';
    if (!form.street.trim()) newErrors.street = 'Street address is required';
    if (!form.city.trim()) newErrors.city = 'Town or area is required';
    if (form.receiver_phone.trim()) {
      const phoneValidation = validatePhoneNumber(form.receiver_phone);
      if (!phoneValidation.valid) newErrors.phone = phoneValidation.error;
    }
    setErrors(newErrors);
    const pinMissing = !form.delivery_lat || !form.delivery_lng;
    setPinError(pinMissing);
    return Object.keys(newErrors).length === 0 && !pinMissing;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!validate()) return;
    onSubmit({
      label: form.label,
      apartment_number: form.apartment_number,
      building: form.building,
      street: form.street.trim(),
      city: form.city.trim(),
      county: form.county,
      landmark: form.landmark,
      receiver_name: form.receiver_name,
      receiver_phone: form.receiver_phone,
      is_default: form.is_default,
      delivery_lat: form.delivery_lat,
      delivery_lng: form.delivery_lng,
    });
  };

  return (
    <div className="bg-white rounded-xl p-6 shadow-sm flex flex-col gap-6">
      {/* 1. Address Type */}
      <div className="flex flex-col gap-2">
        <p className="text-lg font-bold text-gray-900">Address Type</p>
        <div className="flex gap-2">
          {LABELS.map(lbl => (
            <button
              key={lbl}
              type="button"
              onClick={() => handleLabelSelect(lbl)}
              className={
                form.label === lbl
                  ? 'flex-1 bg-gold text-white py-2 rounded-lg text-sm font-semibold transition'
                  : 'flex-1 border border-gray-300 text-gray-500 py-2 rounded-lg text-sm hover:bg-gray-50 transition'
              }
            >
              {lbl}
            </button>
          ))}
        </div>
        {errors.label && (
          <p className="text-sm text-red-500">{errors.label}</p>
        )}
      </div>

      {/* 2. Address Details */}
      <div className="flex flex-col gap-4">
        <p className="text-lg font-bold text-gray-900">Address Details</p>
        <input
          name="apartment_number"
          placeholder="e.g. 12A or Villa 3"
          value={form.apartment_number}
          onChange={handleChange}
          className={inputClass()}
        />
        <input
          name="building"
          placeholder="e.g. Building name or number"
          value={form.building}
          onChange={handleChange}
          className={inputClass()}
        />
        <input
          name="street"
          placeholder="Physical Address / Estate / Street *"
          value={form.street}
          onChange={handleChange}
          className={inputClass(errors.street)}
        />
        {errors.street && (
          <p className="text-sm text-red-500 mt-1">{errors.street}</p>
        )}
        <input
          name="city"
          placeholder="Town or Area * (e.g. Nairobi, Mombasa)"
          value={form.city}
          onChange={handleChange}
          className={inputClass(errors.city)}
        />
        {errors.city && (
          <p className="text-sm text-red-500 mt-1">{errors.city}</p>
        )}
        <input
          name="landmark"
          placeholder="e.g. Landmark, building name, gate colour, or delivery instructions"
          value={form.landmark}
          onChange={handleChange}
          className={inputClass()}
        />
      </div>

      {/* 3. Receiver's Details */}
      <div className="flex flex-col gap-3">
        <p className="text-lg font-bold text-gray-900">Receiver's Details</p>
        <p className="text-sm text-gray-500">Contact information for the person receiving the order</p>
        <div className="grid grid-cols-2 gap-4 mt-3">
          <input
            name="receiver_name"
            placeholder="Receiver name"
            value={form.receiver_name}
            onChange={handleChange}
            className={inputClass()}
          />
          <input
            name="receiver_phone"
            placeholder="e.g. 072123456 or 25472123456"
            value={form.receiver_phone}
            onChange={(e) => {
              handleChange(e);
              if (errors.phone) setErrors(prev => ({ ...prev, phone: '' }));
            }}
            onBlur={() => {
              if (form.receiver_phone.trim()) {
                const result = validatePhoneNumber(form.receiver_phone);
                if (!result.valid) {
                  setErrors(prev => ({ ...prev, phone: result.error }));
                }
              }
            }}
            className={inputClass(errors.phone)}
          />
        </div>
        {errors.phone && (
          <p className="text-sm text-red-500 mt-1">{errors.phone}</p>
        )}
      </div>

      {/* 4. Delivery Location */}
      <div className="border border-gray-200 rounded-lg p-4 bg-gray-50 flex flex-col gap-3">
        <p className="text-lg font-bold text-gray-900">Delivery Location</p>
        <div className="rounded-lg border border-gray-300">
          <DeliveryMapPin
            onPinChange={handlePinChange}
            onCountyDetected={handleCountyDetected}
            initialLat={form.delivery_lat}
            initialLng={form.delivery_lng}
          />
        </div>
        {pinError && (
          <span className="pin-required-hint">
            📍 Click the map or use &ldquo;Use My Location&rdquo; to pin your delivery spot
          </span>
        )}

        {zoneCheckResult?.out_of_range && (
          <div className="border border-red-200 rounded-lg p-4 bg-red-50">
            <div className="flex items-start gap-3">
              <svg className="w-5 h-5 text-red-500 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
              <div>
                <p className="font-medium text-red-800 text-sm">This location is outside our delivery area. Please choose a closer location.</p>
                {zoneCheckResult.distance_km && (
                  <p className="text-sm text-red-500 mt-1">
                    Distance: {Number(zoneCheckResult.distance_km).toFixed(1)} km — outside our delivery range.
                  </p>
                )}
              </div>
            </div>
          </div>
        )}

        {isZoneCheckLoading && (
          <div className="flex items-center gap-2 text-sm text-gray-500">
            <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
            </svg>
            Checking delivery area...
          </div>
        )}
      </div>

      {/* 5. Set this as your default address */}
      <label className="flex items-center gap-2 text-sm text-gray-600">
        <input
          type="checkbox"
          name="is_default"
          checked={form.is_default}
          onChange={handleChange}
          className="rounded text-gold focus:ring-gold"
        />
        Set this as your default address
      </label>

      {/* 6. Submit + Cancel */}
      <div className="flex gap-3">
        <button
          onClick={handleSubmit}
          disabled={zoneCheckResult?.out_of_range === true || isZoneCheckLoading}
          className={`flex-1 bg-gold text-white py-3 rounded-lg transition text-sm font-semibold ${
            zoneCheckResult?.out_of_range === true || isZoneCheckLoading
              ? 'opacity-50 cursor-not-allowed'
              : 'hover:brightness-90'
          }`}
        >
          {submitLabel}
        </button>
        {onCancel && (
          <button
            type="button"
            onClick={onCancel}
            className="flex-1 border border-gray-300 text-gray-500 py-3 rounded-lg hover:bg-gray-50 transition text-sm"
          >
            Cancel
          </button>
        )}
      </div>
    </div>
  );
};

export default AddressForm;
